import { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import { useAuth } from './AuthProvider';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

type Mode = 'artist' | 'buyer';

interface ModeContextType {
  mode: Mode;
  setMode: (mode: Mode) => void;
  isArtist: boolean;
}

const ModeContext = createContext<ModeContextType | undefined>(undefined);

export const useMode = () => {
  const context = useContext(ModeContext);
  if (!context) {
    throw new Error('useMode must be used within ModeProvider');
  }
  return context;
};

export const ModeProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useAuth();
  const [mode, setMode] = useState<Mode>('buyer');
  const [isInitialized, setIsInitialized] = useState(false);

  const { data: profile } = useQuery({
    queryKey: ['profile-mode', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      const { data, error } = await supabase
        .from('profiles')
        .select('user_type')
        .eq('id', user.id)
        .maybeSingle();
      
      if (error) {
        console.error('ModeContext profile fetch error:', error);
        return null;
      }
      
      return data;
    },
    enabled: !!user?.id,
    staleTime: 60000,
    gcTime: 300000,
    refetchOnWindowFocus: false,
  });

  // Artists are users with user_type = 'artist'
  // Artists can also buy, so they're always in "artist" mode but have buyer capabilities
  const isArtist = profile?.user_type === 'artist';

  // Initialize mode based on user type
  useEffect(() => {
    if (!profile || isInitialized) return;

    if (profile.user_type === 'artist') {
      setMode('artist');
    } else {
      setMode('buyer');
    }
    
    setIsInitialized(true);
  }, [profile, isInitialized]);

  return (
    <ModeContext.Provider value={{ mode, setMode, isArtist }}>
      {children}
    </ModeContext.Provider>
  );
};
